import { useTranslation } from "@/i18n";
import { AlertCircle, Info, RefreshCw } from "lucide-react";

/**
 * Props for the ErrorAlert component.
 */
interface ErrorAlertProps {
  /** The error message to display. If null or undefined, nothing is rendered. */
  error?: string | null;
  /** Optional custom CSS class for additional styling */
  className?: string;
}

/**
 * Represents a user-friendly error with title, message, and styling.
 */
interface FriendlyError {
  /** Translation key for the error title */
  titleKey: string;
  /** Translation key for the error message */
  messageKey: string;
  /** Whether this is a temporary/recoverable error (shows different styling) */
  isTemporary: boolean;
  /** Whether to show retry suggestion */
  showRetry: boolean;
}

/**
 * Error patterns to detect and their user-friendly messages.
 * These cover common AI provider errors that don't need to alarm the user.
 */
const ERROR_PATTERNS: Array<{
  patterns: RegExp[];
  friendlyError: FriendlyError;
}> = [
  // Model overloaded / Rate limit errors
  {
    patterns: [
      /overloaded/i,
      /503.*UNAVAILABLE/i,
      /rate.?limit/i,
      /too.?many.?requests/i,
      /capacity/i,
      /server.*busy/i,
    ],
    friendlyError: {
      titleKey: "errors.modelOverloaded.title",
      messageKey: "errors.modelOverloaded.message",
      isTemporary: true,
      showRetry: true,
    },
  },
  // Image processing errors
  {
    patterns: [
      /could.?not.?process.?image/i,
      /image.*processing.*failed/i,
      /invalid.*image/i,
      /unsupported.*image/i,
      /image.*format.*not.*supported/i,
      /cannot.*read.*image/i,
    ],
    friendlyError: {
      titleKey: "errors.imageProcessing.title",
      messageKey: "errors.imageProcessing.message",
      isTemporary: false,
      showRetry: false,
    },
  },
  // Timeout errors
  {
    patterns: [
      /timeout/i,
      /timed.?out/i,
      /request.*took.*too.*long/i,
      /deadline.*exceeded/i,
    ],
    friendlyError: {
      titleKey: "errors.timeout.title",
      messageKey: "errors.timeout.message",
      isTemporary: true,
      showRetry: true,
    },
  },
  // Token/context length errors
  {
    patterns: [
      /context.*length/i,
      /too.*long/i,
      /maximum.*token/i,
      /token.*limit/i,
      /content.*too.*large/i,
    ],
    friendlyError: {
      titleKey: "errors.contextLength.title",
      messageKey: "errors.contextLength.message",
      isTemporary: false,
      showRetry: false,
    },
  },
  // Service unavailable
  {
    patterns: [
      /service.*unavailable/i,
      /temporarily.*unavailable/i,
      /internal.*server.*error/i,
      /500.*error/i,
      /502.*error/i,
      /504.*error/i,
    ],
    friendlyError: {
      titleKey: "errors.serviceUnavailable.title",
      messageKey: "errors.serviceUnavailable.message",
      isTemporary: true,
      showRetry: true,
    },
  },
  // API key / authentication errors (these should not happen in production but handle gracefully)
  {
    patterns: [
      /api.?key.*not.*configured/i,
      /authentication.*failed/i,
      /unauthorized/i,
      /invalid.*api.*key/i,
    ],
    friendlyError: {
      titleKey: "errors.configuration.title",
      messageKey: "errors.configuration.message",
      isTemporary: false,
      showRetry: false,
    },
  },
  // Network/Connection errors
  {
    patterns: [
      /connection.*refused/i,
      /network.*error/i,
      /failed.*to.*fetch/i,
      /ECONNREFUSED/i,
      /ENOTFOUND/i,
      /socket.*hang.*up/i,
    ],
    friendlyError: {
      titleKey: "errors.connection.title",
      messageKey: "errors.connection.message",
      isTemporary: true,
      showRetry: true,
    },
  },
  // Content moderation / Safety errors
  {
    patterns: [
      /content.*policy/i,
      /safety.*filter/i,
      /blocked.*by.*policy/i,
      /harmful.*content/i,
      /content.*flagged/i,
    ],
    friendlyError: {
      titleKey: "errors.contentPolicy.title",
      messageKey: "errors.contentPolicy.message",
      isTemporary: false,
      showRetry: false,
    },
  },
];

/**
 * Analyzes an error message and returns a user-friendly version if it matches known patterns.
 *
 * @param error - The raw error message
 * @returns FriendlyError if a pattern matches, null otherwise
 */
function getFriendlyError(error: string): FriendlyError | null {
  for (const { patterns, friendlyError } of ERROR_PATTERNS) {
    if (patterns.some((pattern) => pattern.test(error))) {
      return friendlyError;
    }
  }
  return null;
}

/**
 * User-friendly error alert component with internationalized messaging.
 * Displays a consistent error UI with an icon and instructions for users.
 * Automatically detects common AI provider errors and shows calming, helpful messages.
 *
 * @param props - Component props
 * @returns Error alert UI or null if no error
 */
export default function ErrorAlert({ error, className = "" }: ErrorAlertProps) {
  const { t } = useTranslation();

  if (!error) return null;

  const friendlyError = getFriendlyError(error);

  // Show friendly message for known temporary/recoverable errors
  if (friendlyError) {
    const bgColor = friendlyError.isTemporary
      ? "bg-amber-500/10 border-amber-500/30"
      : "bg-red-500/10 border-red-500/30";
    const textColor = friendlyError.isTemporary
      ? "text-amber-600"
      : "text-red-500";
    const Icon = friendlyError.isTemporary ? Info : AlertCircle;

    return (
      <div
        className={`${bgColor} border rounded-lg p-4 flex items-start gap-3 ${className}`}
        role="alert"
        aria-live="polite"
      >
        <Icon className={`${textColor} flex-shrink-0 mt-0.5`} size={20} />
        <div className="flex-1">
          <p className={`${textColor} text-sm leading-relaxed`}>
            <span className="font-semibold">{t(friendlyError.titleKey)}</span>
            <br />
            {t(friendlyError.messageKey)}
          </p>
          {friendlyError.showRetry && (
            <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600/80">
              <RefreshCw size={12} />
              <span>{t("errors.retryHint")}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default error display for unknown errors
  const genericTitle = t("errors.generic.title");
  return (
    <div
      className={`bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3 ${className}`}
      role="alert"
      aria-live="assertive"
    >
      <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
      <div className="flex-1">
        <p className="text-red-500 text-sm leading-relaxed">
          <span className="font-semibold">{genericTitle}</span>
          <br />
          {t("errors.generic.message")}
        </p>
        {error && error !== genericTitle && (
          <p className="text-red-400/80 text-xs mt-2 font-mono break-words">
            Details: {error}
          </p>
        )}
      </div>
    </div>
  );
}
